import { useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { useMutation } from '@tanstack/react-query';

import { Button } from '@app/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@app/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@app/components/ui/select';
import { ROUTES } from '@cloud-ui/constants';
import { useCliLoginDialog } from '@cloud-ui/contexts/CliLoginDialogContext';
import { useConfig } from '@cloud-ui/contexts/ConfigContext';
import { useFusionAuth } from '@cloud-ui/contexts/FusionAuthContext';
import { useToast } from '@cloud-ui/contexts/ToastContext';
import { useSwitchOrganization, useUserOrganizations } from '@cloud-ui/hooks/useUser';
import { ORGANIZATION_SESSION_MUTATION_KEY } from '@cloud-ui/utils/authSessionStorage';
import { tryBrowserStorage } from '@cloud-ui/utils/browserStorage';

import {
  Bot,
  CircleUserRound,
  FileText,
  Key,
  LogOut,
  RotateCcwKey,
  Settings,
  User,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { AuthContext } from '../contexts/AuthContext';
import { ACCOUNT_SWITCH_INVITE_KEY } from './AccountRecovery';

type UserMenuItemProps = {
  icon: ReactNode;
  label: string;
  to?: string;
  onSelect?: () => void;
  testId?: string;
};

function UserMenuItem({ icon, label, to, onSelect, testId }: UserMenuItemProps) {
  const className =
    'flex cursor-pointer items-center gap-3 rounded-sm px-3 py-2 text-sm text-foreground outline-none transition-colors focus:bg-primary/10';

  if (to) {
    return (
      <DropdownMenuItem asChild className={className} data-testid={testId}>
        <Link to={to} onClick={onSelect}>
          <span className="text-muted-foreground [&_svg]:size-4">{icon}</span>
          <span>{label}</span>
        </Link>
      </DropdownMenuItem>
    );
  }

  return (
    <DropdownMenuItem className={className} onSelect={() => onSelect?.()} data-testid={testId}>
      <span className="text-muted-foreground [&_svg]:size-4">{icon}</span>
      <span>{label}</span>
    </DropdownMenuItem>
  );
}

export default function LoggedInAs() {
  const navigate = useNavigate();
  const fusionAuth = useFusionAuth();
  const { user, logout, reloadSession, organization, isAdmin } = useContext(AuthContext) || {};
  const { data: organizations } = useUserOrganizations();
  const { mutate: switchOrganization } = useSwitchOrganization();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { config } = useConfig();
  const { openCliLoginDialog } = useCliLoginDialog();
  const { showToast } = useToast();

  const sortedOrganizations = useMemo(
    () => [...(organizations ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [organizations],
  );

  const handleClose = () => {
    setIsMenuOpen(false);
  };

  const logoutMutation = useMutation({
    mutationKey: ORGANIZATION_SESSION_MUTATION_KEY,
    onMutate: handleClose,
    mutationFn: async () => {
      const logoutUrl = fusionAuth?.fusionAuthConfig.useFusionAuth
        ? fusionAuth.buildLogoutUrl()
        : ROUTES.login;
      if (!logoutUrl) {
        throw new Error('Logout is not configured');
      }
      await logout?.();
      tryBrowserStorage(() => sessionStorage.removeItem(ACCOUNT_SWITCH_INVITE_KEY));
      reloadSession?.(logoutUrl);
    },
    onError: (error) => {
      console.error('Unable to log out:', error);
      showToast('Unable to log out. Please try again.', 'error');
    },
  });

  const handleCliLoginInfo = () => {
    openCliLoginDialog();
    handleClose();
  };

  const handlePasswordReset = () => {
    navigate(ROUTES.loggedInPasswordReset);
    handleClose();
  };

  if (!user) {
    return (
      <Button asChild variant="ghost" size="icon" aria-label="Sign in">
        <Link to={ROUTES.login}>
          <CircleUserRound className="size-5" />
        </Link>
      </Button>
    );
  }

  return (
    <DropdownMenu modal={false} open={isMenuOpen} onOpenChange={setIsMenuOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          data-testid="user-menu-button"
          disabled={logoutMutation.isPending}
          aria-label="account of current user"
          aria-controls="menu-appbar"
          aria-haspopup="true"
        >
          <User className="size-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        id="menu-appbar"
        align="end"
        sideOffset={10}
        className="min-w-[220px] overflow-visible rounded-md border border-border bg-popover p-0 text-popover-foreground shadow-lg"
      >
        <div className="p-4 pb-3">
          <div className="flex flex-col items-center rounded-md bg-muted/40 p-3 text-center shadow-sm">
            <CircleUserRound className="mb-2 size-12 text-primary" />
            <div className="w-full min-w-0">
              <p className="text-sm font-bold text-foreground">Logged in as</p>
              <p className="truncate text-sm text-foreground">{user.name || user.email}</p>
              <p className="truncate text-xs text-muted-foreground">{user.email}</p>
              {sortedOrganizations.length > 1 && (
                <div
                  className="mt-3 w-full"
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  <Select
                    value={organization?.id}
                    onValueChange={(organizationId) => switchOrganization(organizationId)}
                  >
                    <SelectTrigger size="sm" label="Organization" className="bg-background">
                      <SelectValue placeholder="Organization" />
                    </SelectTrigger>
                    <SelectContent>
                      {sortedOrganizations.map((candidateOrganization) => (
                        <SelectItem key={candidateOrganization.id} value={candidateOrganization.id}>
                          {candidateOrganization.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="px-1 pb-1">
          <UserMenuItem
            icon={<User />}
            label="My API Tokens"
            to={ROUTES.apiTokens}
            onSelect={handleClose}
          />
          <UserMenuItem
            icon={<Settings />}
            label="Organization Settings"
            to={`/organizations/${organization?.id}`}
            onSelect={handleClose}
            testId="org-settings-menu-item"
          />
          {config?.enableServerSettingsPage && isAdmin && (
            <UserMenuItem
              icon={<Bot />}
              label="Red Team Providers"
              to={ROUTES.serverSettings.index}
              onSelect={handleClose}
            />
          )}
          <DropdownMenuSeparator className="my-1 bg-border" />
          <UserMenuItem
            icon={<Key />}
            label="CLI Login Information"
            onSelect={handleCliLoginInfo}
          />
          {config?.useFusionAuth && config?.isPromptfooCloud && (
            <UserMenuItem
              icon={<RotateCcwKey />}
              label="Reset Password"
              onSelect={handlePasswordReset}
            />
          )}
          <UserMenuItem
            icon={<FileText />}
            label="Changelog"
            to={ROUTES.changelog}
            onSelect={handleClose}
          />
          <UserMenuItem
            icon={<LogOut />}
            label="Logout"
            onSelect={() => logoutMutation.mutate()}
            testId="logout-menu-item"
          />
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
